import { Bug, ChevronDown, Gift } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PageHeader } from '@/components/common/page-header';
import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BUG_REPORT_PAGE_SIZE,
  useBugBountyCampaign,
  useBugReports,
  useUpdateBugBountyCampaign,
} from '@/hooks/use-bug-reports';
import { ApiError } from '@/lib/api-client';
import type { BugReportFilter } from '@/types/bug-reports';

const FILTERS: { value: BugReportFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'granted', label: 'Granted' },
];

/** ISO → something an operator can scan. */
function formatSubmitted(iso: string): string {
  const date = new Date(iso);

  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Bug reports — the bug-bounty confirm queue (PRD-0007 R10/R11).
 *
 * The endpoints behind this shipped in August; this page did not, so 638 submissions from 150
 * users accumulated where nobody could read them while the Android Home card went on
 * advertising the reward to 419 users who tapped it.
 *
 * Two things therefore live on one screen. The queue itself, every status rather than only the
 * open ones — the API filtered `status = 'open'` until R10, which is why there was no history
 * view and no pagination. And the campaign's two switches, because the row seeds `is_active =
 * false` AND `max_grants = 0`: without the banner an operator presses Confirm and reads a bare
 * `409` that cannot say which switch is down.
 */
export function SupportBugReportsPage(): JSX.Element {
  const [filter, setFilter] = useState<BugReportFilter>('all');
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [campaignConfirmOpen, setCampaignConfirmOpen] = useState(false);

  const page = useBugReports(filter, offset);
  const campaign = useBugBountyCampaign();
  const updateCampaign = useUpdateBugBountyCampaign();

  function changeFilter(next: BugReportFilter): void {
    setFilter(next);
    // A filter change re-slices the queue, so an offset from the previous slice is meaningless
    // — holding it would land the operator on page 3 of a two-page list and show nothing.
    setOffset(0);
    setExpanded(null);
  }

  function handleCampaignToggle(): void {
    const current = campaign.data;

    if (!current) return;

    // Uncapped on enable, per PRD-0007's no-total-cap decision (reaffirmed 2026-10-11). The
    // cap stays whatever it is when switching OFF, so turning the campaign off and on again
    // does not silently widen it.
    updateCampaign.mutate(
      { isActive: !current.isActive, maxGrants: current.isActive ? current.maxGrants : null },
      {
        onSuccess: (next) => {
          toast.success(
            next.isActive
              ? 'Bounty campaign enabled — confirmed reports now grant Premium.'
              : 'Bounty campaign disabled — confirms will be refused.'
          );
          setCampaignConfirmOpen(false);
        },
        onError: (err) => {
          setCampaignConfirmOpen(false);
          toast.error(
            err instanceof ApiError ? err.message : 'Could not update the campaign. Try again.'
          );
        },
      }
    );
  }

  const total = page.data?.total ?? 0;
  const hasPrev = offset > 0;
  const hasNext = offset + BUG_REPORT_PAGE_SIZE < total;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bug reports"
        description="The bug-bounty confirm queue. Confirming a report grants the reporter Premium and is recorded against your operator account."
      />

      {campaign.isError && (
        <ErrorState
          title="Couldn't load the bounty campaign."
          error={campaign.error}
          onRetry={() => void campaign.refetch()}
        />
      )}

      {campaign.data && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-base">
                <Gift className="h-4 w-4 text-primary" aria-hidden />
                Bounty campaign
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {campaign.data.rewardMonths} months of Premium per confirmed report. A confirm is
                refused unless the campaign is active and under its cap.
              </p>
            </div>
            <Button
              variant={campaign.data.isActive ? 'outline' : 'default'}
              disabled={updateCampaign.isPending}
              onClick={() => setCampaignConfirmOpen(true)}
            >
              {campaign.data.isActive ? 'Disable' : 'Enable'}
            </Button>
          </CardHeader>

          <CardContent className="flex flex-wrap items-center gap-2">
            <Badge variant={campaign.data.isActive ? 'default' : 'outline'}>
              {campaign.data.isActive ? 'Active' : 'Inactive'}
            </Badge>
            <Badge variant="outline">
              {campaign.data.maxGrants === null
                ? `${String(campaign.data.grantsIssued)} granted · uncapped`
                : `${String(campaign.data.grantsIssued)} of ${String(campaign.data.maxGrants)} granted`}
            </Badge>
            {!campaign.data.canGrant && (
              <span className="text-sm text-destructive" role="alert">
                {campaign.data.isActive
                  ? 'The cap is spent — a confirm will be refused until it is raised.'
                  : 'The campaign is inactive — a confirm will be refused.'}
              </span>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((option) => (
          <Button
            key={option.value}
            size="sm"
            variant={filter === option.value ? 'default' : 'outline'}
            onClick={() => changeFilter(option.value)}
          >
            {option.label}
            {page.data && option.value !== 'all' && (
              <span className="ml-1 text-xs opacity-70">
                {page.data.counts[option.value] ?? 0}
              </span>
            )}
          </Button>
        ))}
      </div>

      {page.isLoading && <TableSkeleton rows={8} />}

      {page.isError && (
        <ErrorState
          title="Couldn't load bug reports."
          error={page.error}
          onRetry={() => void page.refetch()}
        />
      )}

      {page.data && page.data.items.length === 0 && (
        <EmptyState
          message={
            filter === 'all' ? 'No bug reports submitted yet' : `No ${filter} bug reports`
          }
        />
      )}

      {page.data && page.data.items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.data.items.map((report) => (
                  <TableRow key={report.reference}>
                    <TableCell className="font-mono text-xs">{report.reference}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatSubmitted(report.createdAt)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {report.userId === null ? (
                        // An anonymous ticket has nobody to grant to, which the confirm
                        // endpoint rejects — say so here rather than at the 400.
                        <Badge variant="outline">anonymous</Badge>
                      ) : (
                        <span className="font-mono text-xs">user {report.userId}</span>
                      )}
                      {report.contactEmail && (
                        <div className="text-xs text-muted-foreground">{report.contactEmail}</div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <button
                        type="button"
                        className="flex w-full items-start gap-1 text-left text-sm hover:underline"
                        onClick={() =>
                          setExpanded(expanded === report.reference ? null : report.reference)
                        }
                        aria-expanded={expanded === report.reference}
                      >
                        <ChevronDown
                          className={`mt-0.5 h-3 w-3 shrink-0 transition-transform ${
                            expanded === report.reference ? 'rotate-180' : ''
                          }`}
                          aria-hidden
                        />
                        <span
                          className={expanded === report.reference ? 'whitespace-pre-wrap' : 'line-clamp-2'}
                        >
                          {report.body}
                        </span>
                      </button>

                      {expanded === report.reference && report.diagnostics && (
                        <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-xs">
                          {JSON.stringify(report.diagnostics, null, 2)}
                        </pre>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={report.status === 'granted' ? 'default' : 'outline'}>
                        {report.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/support/bug-reports/${report.reference}`}>View</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {page.data && total > BUG_REPORT_PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {offset + 1}–{Math.min(offset + BUG_REPORT_PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!hasPrev}
              onClick={() => setOffset(Math.max(offset - BUG_REPORT_PAGE_SIZE, 0))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!hasNext}
              onClick={() => setOffset(offset + BUG_REPORT_PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={campaignConfirmOpen}
        onOpenChange={setCampaignConfirmOpen}
        title={campaign.data?.isActive ? 'Disable the bounty campaign?' : 'Enable the bounty campaign?'}
        description={
          campaign.data?.isActive ? (
            <span>
              Confirms will be refused while the campaign is off. The Android Home card keeps
              advertising the reward either way.
            </span>
          ) : (
            <span>
              Confirmed reports will immediately grant {campaign.data?.rewardMonths ?? 12} months
              of Premium, <strong>uncapped</strong>. There is no automatic pause and no
              grant-volume alert, so the only control on how much is paid out is you reading each
              report before confirming it.
            </span>
          )
        }
        confirmLabel={campaign.data?.isActive ? 'Disable' : 'Enable'}
        variant={campaign.data?.isActive ? 'default' : 'destructive'}
        pending={updateCampaign.isPending}
        onConfirm={handleCampaignToggle}
      />

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Bug className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
        There is no reject action: a report leaves this queue only by being granted. Duplicates
        and noise stay open until that changes (PRD-0007 R10).
      </p>
    </div>
  );
}
